// The site's structure as the Structure tab edits it: a local copy of the ordered sections that every
// mode (list, constructor, AI) changes at once, saved through PUT /admin/websites/{id}/structure
// shortly after each change («Структура сохраняется автоматически»). Saves run one at a time; the
// server's answer replaces the copy only when no newer edit is waiting.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { saveStructure, structureKey, structureQuery, type PinnedPreset, type SectionIn, type SectionOut, type StructureOut } from './api';

/** One section of the page: its type and 0–N pinned preset versions (C4; the first is the primary). */
export type Sec = {
  /** a client-side key: stable while the PM edits (the server's ids change on every save) */
  key: string;
  section_type: string;
  presets: PinnedPreset[];
  content_source: SectionOut['content_source'];
  content: SectionOut['content'];
  notes: string | null;
  ai_reason: string | null;
};

let seq = 0;
export const newKey = () => `s${++seq}`;

/** A new section of a type, without a design («AI подберёт»). */
export function blankSection(section_type: string, presets: PinnedPreset[] = []): Sec {
  return { key: newKey(), section_type, presets, content_source: 'research', content: {}, notes: null, ai_reason: null };
}

function fromServer(out: StructureOut, keys: string[] = []): Sec[] {
  return out.sections.map((s, i) => ({
    key: keys[i] ?? newKey(),
    section_type: s.section_type,
    presets: s.presets,
    content_source: s.content_source,
    content: s.content,
    notes: s.notes,
    ai_reason: s.ai_reason,
  }));
}

function toServer(secs: Sec[]): SectionIn[] {
  return secs.map((s) => ({
    section_type: s.section_type,
    content_source: s.content_source,
    content: s.content,
    notes: s.notes,
    ai_reason: s.ai_reason,
    presets: s.presets.map((p) => p.preset_version_id),
  }));
}

export const SAVE_DELAY_MS = 250;

export type StructureDoc = {
  secs: Sec[];
  loading: boolean;
  error: unknown;
  /** apply a change; it is saved shortly after */
  update: (fn: (secs: Sec[]) => Sec[]) => void;
  /** save now and resolve with the server's copy (e.g. before generating) */
  flush: () => Promise<void>;
  /** replace the copy with a structure the server already holds (AI mode's applied suggestion) */
  adopt: (out: StructureOut) => void;
  retry: () => void;
};

export function useStructureDoc(siteId: string, onSaveError: (e: unknown) => void): StructureDoc {
  const qc = useQueryClient();
  const q = useQuery(structureQuery(siteId));
  const [secs, setSecs] = useState<Sec[] | null>(null);
  const latest = useRef<Sec[] | null>(null);
  const edits = useRef(0);
  const saving = useRef<Promise<void> | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const onError = useRef(onSaveError);
  useEffect(() => {
    onError.current = onSaveError;
  });

  // The first answer (and refetches while nothing is being edited) seed the copy.
  useEffect(() => {
    if (!q.data || saving.current || timer.current !== undefined) return;
    const next = fromServer(q.data, latest.current?.map((s) => s.key));
    latest.current = next;
    setSecs(next);
  }, [q.data]);

  const runSave = useCallback((): Promise<void> => {
    if (saving.current) return saving.current.then(() => runSave());
    const snapshot = latest.current;
    if (!snapshot) return Promise.resolve();
    const at = edits.current;
    const p = saveStructure(siteId, toServer(snapshot))
      .then((out) => {
        // Only adopt the answer when the PM has not changed anything since.
        const keys = snapshot.map((s) => s.key);
        qc.setQueryData(structureKey(siteId), out);
        if (edits.current === at && timer.current === undefined) {
          const next = fromServer(out, keys);
          latest.current = next;
          setSecs(next);
        }
      })
      .catch((e: unknown) => {
        onError.current(e);
        void qc.invalidateQueries({ queryKey: structureKey(siteId) }).then(() => {
          const fresh = qc.getQueryData<StructureOut>(structureKey(siteId));
          if (fresh && timer.current === undefined) {
            const next = fromServer(fresh);
            latest.current = next;
            setSecs(next);
          }
        });
      })
      .finally(() => {
        saving.current = null;
      });
    saving.current = p;
    return p;
  }, [qc, siteId]);

  const update = useCallback(
    (fn: (secs: Sec[]) => Sec[]) => {
      const base = latest.current ?? [];
      const next = fn(base);
      if (next === base) return;
      latest.current = next;
      edits.current += 1;
      setSecs(next);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = undefined;
        void runSave();
      }, SAVE_DELAY_MS);
    },
    [runSave],
  );

  const flush = useCallback(async () => {
    if (timer.current !== undefined) {
      window.clearTimeout(timer.current);
      timer.current = undefined;
      await runSave();
    } else if (saving.current) await saving.current;
  }, [runSave]);

  const adopt = useCallback(
    (out: StructureOut) => {
      window.clearTimeout(timer.current);
      timer.current = undefined;
      qc.setQueryData(structureKey(siteId), out);
      const next = fromServer(out);
      latest.current = next;
      edits.current += 1;
      setSecs(next);
    },
    [qc, siteId],
  );

  // Leaving the tab saves what is still waiting.
  useEffect(
    () => () => {
      if (timer.current !== undefined) {
        window.clearTimeout(timer.current);
        timer.current = undefined;
        void runSave();
      }
    },
    [runSave],
  );

  return { secs: secs ?? [], loading: !secs && !q.error, error: q.error, update, flush, adopt, retry: () => void q.refetch() };
}

/** The pinned preset the previews show for a section: the first (primary) one. */
export const primary = (s: Sec | undefined): PinnedPreset | null => s?.presets[0] ?? null;

// ── Pure edits (every mode uses them; unit-tested) ──

/** The API's limit of pinned versions per section (SectionIn.presets max_length). */
export const MAX_PINS = 8;

/** Where a new section goes when no position is given: before the footer (design: insertAt). */
export function defaultInsertAt(secs: Sec[]): number {
  return secs.length && secs[secs.length - 1]?.section_type === 'footer' ? secs.length - 1 : secs.length;
}

export function insertSection(secs: Sec[], sec: Sec, at: number = defaultInsertAt(secs)): Sec[] {
  const next = [...secs];
  next.splice(Math.max(0, Math.min(at, next.length)), 0, sec);
  return next;
}

export function removeSection(secs: Sec[], i: number): Sec[] {
  return secs.filter((_, j) => j !== i);
}

export function moveSection(secs: Sec[], from: number, to: number): Sec[] {
  if (from === to || from < 0 || to < 0 || from >= secs.length || to >= secs.length) return secs;
  const next = [...secs];
  const [m] = next.splice(from, 1);
  next.splice(to, 0, m as Sec);
  return next;
}

/**
 * C4 (PLAN D21): a section holds 0–N designs. Choosing one that is not pinned makes it the primary
 * (the previews show it), keeping the others as references; choosing a pinned one removes it.
 * Returns null when the section already holds the maximum.
 */
export function togglePin(sec: Sec, pin: PinnedPreset): Sec | null {
  if (sec.presets.some((p) => p.preset_id === pin.preset_id)) return { ...sec, presets: sec.presets.filter((p) => p.preset_id !== pin.preset_id) };
  if (sec.presets.length >= MAX_PINS) return null;
  return { ...sec, presets: [pin, ...sec.presets] };
}

/** «Сменить дизайн» (the constructor): the design becomes the primary; the other references stay. */
export function setPrimary(sec: Sec, pin: PinnedPreset | null): Sec {
  if (!pin) return { ...sec, presets: [] };
  return { ...sec, presets: [pin, ...sec.presets.filter((p) => p.preset_id !== pin.preset_id)].slice(0, MAX_PINS) };
}

export function updateAt(secs: Sec[], i: number, fn: (s: Sec) => Sec): Sec[] {
  return secs.map((s, j) => (j === i ? fn(s) : s));
}

export { toServer };
